import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  BookOpen,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Clock,
  Printer,
  ShieldCheck,
  RefreshCw,
  Eye,
  FileText,
  Sliders,
  RotateCcw,
  Check,
  X,
  Plus,
  HelpCircle,
  FileCheck2,
  History,
  Award,
  Search,
  Layers,
  FileSpreadsheet,
  Download,
  AlertTriangle,
  Upload,
  Calendar,
  Hash,
  Scale
} from 'lucide-react';

export const PYQCorpusManagerPage: React.FC = () => {
  const { fetchWithAuth, user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.role === 'admin' || user?.role === 'principal';

  type TabType =
    | 'overview'
    | 'matrix'
    | 'import-queue'
    | 'verified-papers'
    | 'pending'
    | 'questions'
    | 'answers'
    | 'solutions'
    | 'missing'
    | 'coverage';

  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Filters
  const [selectedBoard, setSelectedBoard] = useState<number>(2); // 1: HBSE, 2: CBSE
  const [selectedClass, setSelectedClass] = useState<number>(1); // 1: Class 10, 2: Class 12
  const [selectedYear, setSelectedYear] = useState<number>(2024);

  // States
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<any>(null);
  const [matrix, setMatrix] = useState<any[]>([]);
  const [papers, setPapers] = useState<any[]>([]);
  const [selectedPaper, setSelectedPaper] = useState<any>(null);
  const [paperDetails, setPaperDetails] = useState<any>(null);
  const [missingPapers, setMissingPapers] = useState<any[]>([]);
  const [completeness, setCompleteness] = useState<any>(null);
  const [coverageData, setCoverageData] = useState<any>(null);

  // Import Modal States
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFilename, setImportFilename] = useState('');
  const [importYear, setImportYear] = useState(2024);
  const [importSet, setImportSet] = useState('Set 1');
  const [importSource, setImportSource] = useState('Official Board Portal');
  const [importUrl, setImportUrl] = useState('');
  const [importStatusMsg, setImportStatusMsg] = useState<{ success: boolean; text: string } | null>(null);

  // Load Corpus Data
  const loadCorpusData = async () => {
    setLoading(true);
    try {
      // 1. Overview
      const ovRes = await fetchWithAuth('/api/pyq-corpus/overview');
      const ovData = await ovRes.json();
      if (ovData.success) setOverview(ovData);

      // 2. Matrix
      const matRes = await fetchWithAuth(`/api/pyq-corpus/matrix?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const matData = await matRes.json();
      if (matData.success) setMatrix(matData.matrix || []);

      // 3. Papers List
      const papRes = await fetchWithAuth(`/api/pyq-corpus/papers?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const papData = await papRes.json();
      if (papData.success && papData.papers) {
        setPapers(papData.papers);
        if (papData.papers.length > 0 && !selectedPaper) {
          inspectPaperDetail(papData.papers[0].id);
        }
      }

      // 4. Missing Papers
      const misRes = await fetchWithAuth('/api/pyq-corpus/missing');
      const misData = await misRes.json();
      if (misData.success) setMissingPapers(misData.missing_papers || []);

      // 5. Completeness
      const comRes = await fetchWithAuth('/api/pyq-corpus/completeness');
      const comData = await comRes.json();
      if (comData.success) setCompleteness(comData);

      // 6. Coverage
      const covRes = await fetchWithAuth('/api/pyq-corpus/coverage');
      const covData = await covRes.json();
      if (covData.success) setCoverageData(covData);
    } catch (err) {
      console.error('Failed to load PYQ corpus data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCorpusData();
  }, [selectedBoard, selectedClass]);

  const inspectPaperDetail = async (paperId: number) => {
    try {
      const res = await fetchWithAuth(`/api/pyq-corpus/papers/${paperId}`);
      const data = await res.json();
      if (data.success) {
        setPaperDetails(data);
        setSelectedPaper(data.paper);
      }
    } catch (err) {
      console.error('Error fetching paper details:', err);
    }
  };

  const handleImportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!importFilename) return;
    try {
      const res = await fetchWithAuth('/api/pyq-corpus/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          board_id: selectedBoard,
          class_id: selectedClass,
          exam_year: importYear,
          set_name: importSet,
          original_filename: importFilename,
          source_name: importSource,
          source_url: importUrl || null
        })
      });
      const data = await res.json();
      if (data.success) {
        setImportStatusMsg({ success: true, text: 'Paper successfully imported with SHA-256 hash.' });
        loadCorpusData();
        setTimeout(() => setShowImportModal(false), 1500);
      } else {
        setImportStatusMsg({ success: false, text: data.message || 'Import failed' });
      }
    } catch (err: any) {
      setImportStatusMsg({ success: false, text: err.message || 'Import failed' });
    }
  };

  const handleVerifyPaper = async (paperId: number) => {
    try {
      const res = await fetchWithAuth(`/api/pyq-corpus/${paperId}/verify`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        alert('Paper officially verified!');
        loadCorpusData();
        if (selectedPaper) inspectPaperDetail(selectedPaper.id);
      }
    } catch (err) {
      console.error('Verification error:', err);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#0F2C59] via-[#1E3A8A] to-[#1E40AF] text-white p-6 rounded-2xl shadow-md border-l-4 border-amber-400">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2 text-amber-300 text-xs font-black uppercase tracking-wider">
              <BookOpen className="w-4 h-4" />
              <span>Phase 21 Production Engine • District Nuh, Haryana</span>
            </div>
            <h1 className="text-xl md:text-2xl font-black text-white mt-1">
              Real PYQ Corpus Manager & Source Verification System
            </h1>
            <p className="text-xs text-slate-200 mt-1 max-w-3xl font-medium">
              Factual, source-traceable repository of genuine HBSE and CBSE Mathematics previous-year exam papers with SHA-256 integrity, OCR mathematical normalization, step derivations, and missing paper audit tracking.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 bg-amber-400 hover:bg-amber-500 text-slate-950 rounded-xl text-xs font-black flex items-center gap-1.5 shadow-md"
            >
              <Upload className="w-3.5 h-3.5" />
              <span>Import PYQ Paper</span>
            </button>
          </div>
        </div>
      </div>

      {/* Global Board & Class Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-700">Board:</span>
            <div className="flex gap-1">
              <button
                onClick={() => setSelectedBoard(1)}
                className={`px-3 py-1 rounded-lg font-bold text-xs transition ${
                  selectedBoard === 1 ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'
                }`}
              >
                HBSE (Haryana)
              </button>
              <button
                onClick={() => setSelectedBoard(2)}
                className={`px-3 py-1 rounded-lg font-bold text-xs transition ${
                  selectedBoard === 2 ? 'bg-amber-500 text-slate-950' : 'bg-slate-100 text-slate-700'
                }`}
              >
                CBSE (Central)
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-700">Class:</span>
            <select
              value={selectedClass}
              onChange={e => setSelectedClass(Number(e.target.value))}
              className="p-1.5 bg-slate-50 border rounded-lg font-bold text-slate-800"
            >
              <option value={1}>Class 10 (Secondary)</option>
              <option value={2}>Class 12 (Senior Secondary)</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadCorpusData}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-bold flex items-center gap-1"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh Corpus</span>
          </button>
        </div>
      </div>

      {/* 10-Tab Navigation Bar */}
      <div className="flex overflow-x-auto border-b border-slate-200 bg-white rounded-t-xl p-1 gap-1 text-xs font-bold shadow-xs">
        {[
          { id: 'overview', label: '1. Overview', icon: BookOpen, color: 'text-amber-400' },
          { id: 'matrix', label: '2. Year Matrix', icon: Calendar, color: 'text-emerald-400' },
          { id: 'verified-papers', label: '3. Verified Papers', icon: ShieldCheck, color: 'text-blue-400' },
          { id: 'questions', label: '4. Questions', icon: FileText, color: 'text-purple-400' },
          { id: 'answers', label: '5. Answers', icon: CheckCircle2, color: 'text-indigo-400' },
          { id: 'solutions', label: '6. Step Solutions', icon: Sparkles, color: 'text-rose-400' },
          { id: 'missing', label: '7. Missing Papers', icon: AlertTriangle, color: 'text-orange-400' },
          { id: 'coverage', label: '8. Board Comparison', icon: Scale, color: 'text-teal-400' }
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition whitespace-nowrap ${
                isActive ? 'bg-[#0F2C59] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${tab.color}`} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ----------------- TAB 1: OVERVIEW ----------------- */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {overview && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-xs space-y-1">
                <div className="text-slate-500 font-bold text-xs uppercase">Total Real Papers</div>
                <div className="text-2xl font-black text-slate-900">{overview.total_papers}</div>
                <div className="text-[10px] text-slate-500 font-bold">Genuine Board Archive</div>
              </div>

              <div className="p-4 bg-white rounded-2xl border border-emerald-200 bg-emerald-50/30 shadow-xs space-y-1">
                <div className="text-emerald-800 font-bold text-xs uppercase">Verified Papers</div>
                <div className="text-2xl font-black text-emerald-950">{overview.verified_papers}</div>
                <div className="text-[10px] text-emerald-700 font-bold">100% SHA-256 Hashed</div>
              </div>

              <div className="p-4 bg-white rounded-2xl border border-blue-200 bg-blue-50/30 shadow-xs space-y-1">
                <div className="text-blue-800 font-bold text-xs uppercase">Extracted Questions</div>
                <div className="text-2xl font-black text-blue-950">{overview.questions.total}</div>
                <div className="text-[10px] text-blue-700 font-bold">Normalized Math</div>
              </div>

              <div className="p-4 bg-white rounded-2xl border border-purple-200 bg-purple-50/30 shadow-xs space-y-1">
                <div className="text-purple-800 font-bold text-xs uppercase">Missing / Unavailable</div>
                <div className="text-2xl font-black text-purple-950">{overview.missing_papers_count}</div>
                <div className="text-[10px] text-purple-700 font-bold">Logged In Tracker</div>
              </div>
            </div>
          )}

          {/* Breakdown Table */}
          {overview && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-3 text-xs">
              <h3 className="font-black text-slate-900 text-sm">Factual Real Papers Distribution</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-slate-50 rounded-xl border">
                  <div className="font-bold text-slate-600">HBSE Class 10:</div>
                  <div className="text-lg font-black text-slate-900">{overview.breakdown.hbse_class_10} Papers</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl border">
                  <div className="font-bold text-slate-600">CBSE Class 10:</div>
                  <div className="text-lg font-black text-slate-900">{overview.breakdown.cbse_class_10} Papers</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl border">
                  <div className="font-bold text-slate-600">HBSE Class 12:</div>
                  <div className="text-lg font-black text-slate-900">{overview.breakdown.hbse_class_12} Papers</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl border">
                  <div className="font-bold text-slate-600">CBSE Class 12:</div>
                  <div className="text-lg font-black text-slate-900">{overview.breakdown.cbse_class_12} Papers</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ----------------- TAB 2: YEAR MATRIX ----------------- */}
      {activeTab === 'matrix' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4 text-xs">
          <div className="flex justify-between items-center border-b pb-3">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-600" />
              Year & Set Corpus Availability Matrix
            </h3>
            <span className="text-slate-500 font-bold text-[11px]">
              {selectedBoard === 1 ? 'HBSE' : 'CBSE'} Class {selectedClass === 1 ? '10' : '12'}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b bg-slate-50 text-slate-700 font-bold uppercase text-[10px]">
                  <th className="py-3 px-4">Year</th>
                  <th className="py-3 px-4">Exam Type</th>
                  <th className="py-3 px-4">Set / Code</th>
                  <th className="py-3 px-4">Medium</th>
                  <th className="py-3 px-4">Source</th>
                  <th className="py-3 px-4">Verification</th>
                  <th className="py-3 px-4">Publication</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {matrix.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="py-3 px-4 font-black text-slate-900">{p.exam_year}</td>
                    <td className="py-3 px-4 font-bold text-slate-700">{p.exam_type}</td>
                    <td className="py-3 px-4 font-mono font-bold text-indigo-900">{p.set_name} ({p.paper_code || 'N/A'})</td>
                    <td className="py-3 px-4 text-slate-600">{p.medium}</td>
                    <td className="py-3 px-4 text-slate-600">{p.source_name}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-900 rounded font-bold text-[10px]">
                        {p.verification_status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-900 rounded font-bold text-[10px]">
                        {p.publication_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ----------------- TAB 3: VERIFIED PAPERS ----------------- */}
      {activeTab === 'verified-papers' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Papers Selector */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs space-y-3">
            <div className="font-black text-slate-900 text-sm border-b pb-2">Verified Papers List</div>
            <div className="space-y-2 max-h-[550px] overflow-y-auto pr-1">
              {papers.map((p) => (
                <div
                  key={p.id}
                  onClick={() => inspectPaperDetail(p.id)}
                  className={`p-3 rounded-xl border transition cursor-pointer text-xs space-y-1 ${
                    selectedPaper?.id === p.id
                      ? 'bg-blue-50 border-blue-500 shadow-xs'
                      : 'bg-slate-50 hover:bg-slate-100 border-slate-200'
                  }`}
                >
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="font-bold text-slate-900">{p.exam_year} • {p.set_name}</span>
                    <span className="text-emerald-700 font-bold">{p.verification_status}</span>
                  </div>
                  <div className="font-medium text-slate-800 line-clamp-1">{p.original_filename}</div>
                  <div className="text-[10px] text-slate-500 font-mono">Hash: {p.file_hash?.substring(0, 16)}...</div>
                </div>
              ))}
            </div>
          </div>

          {/* Paper 360 View */}
          {selectedPaper && (
            <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4 text-xs">
              <div className="flex justify-between items-start border-b pb-3">
                <div>
                  <h3 className="text-base font-black text-slate-900">{selectedPaper.source_title || selectedPaper.original_filename}</h3>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {selectedPaper.board_code} Class {selectedPaper.class_name} • {selectedPaper.exam_year} ({selectedPaper.set_name})
                  </div>
                </div>

                {isAdmin && selectedPaper.verification_status !== 'OFFICIAL_VERIFIED' && (
                  <button
                    onClick={() => handleVerifyPaper(selectedPaper.id)}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold"
                  >
                    Verify Paper
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="p-3 bg-slate-50 rounded-xl border">
                  <div className="text-slate-500 text-[10px] font-bold uppercase">Paper Code:</div>
                  <div className="font-mono font-bold text-slate-900">{selectedPaper.paper_code || 'Standard'}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl border">
                  <div className="text-slate-500 text-[10px] font-bold uppercase">Total Marks:</div>
                  <div className="font-bold text-slate-900">{selectedPaper.total_marks} Marks ({selectedPaper.duration_minutes} Mins)</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl border">
                  <div className="text-slate-500 text-[10px] font-bold uppercase">Source Type:</div>
                  <div className="font-bold text-indigo-900">{selectedPaper.source_type}</div>
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border font-mono text-[10px] text-slate-600 break-all space-y-1">
                <div className="font-bold text-slate-800 uppercase">SHA-256 File Hash:</div>
                <div>{selectedPaper.file_hash}</div>
              </div>

              {selectedPaper.source_url && (
                <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-200 text-xs">
                  <span className="font-bold text-blue-900">Source Link: </span>
                  <a href={selectedPaper.source_url} target="_blank" rel="noreferrer" className="text-blue-600 underline font-mono text-[11px]">
                    {selectedPaper.source_url}
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ----------------- TAB 7: MISSING PAPERS TRACKER ----------------- */}
      {activeTab === 'missing' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4 text-xs">
          <div className="flex justify-between items-center border-b pb-3">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-600" />
              Missing / Unavailable Papers Tracker ({missingPapers.length} Records)
            </h3>
          </div>

          <div className="divide-y divide-slate-100">
            {missingPapers.map((m) => (
              <div key={m.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900">
                    {m.board_code} Class {m.class_name} • {m.exam_year} ({m.set_name})
                  </div>
                  <div className="text-slate-500 text-[11px] mt-0.5">{m.notes}</div>
                </div>
                <span className="px-2.5 py-1 bg-orange-100 text-orange-950 font-bold rounded text-[10px]">
                  {m.reason}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 8: BOARD COMPARISON ----------------- */}
      {activeTab === 'coverage' && coverageData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
          <div className="p-6 bg-white rounded-2xl border-2 border-emerald-200 shadow-sm space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="font-black text-emerald-950 text-sm">{coverageData.hbse.board_name}</h3>
              <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-900 rounded font-bold text-[10px]">HBSE</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between font-bold">
                <span className="text-slate-600">Papers Available:</span>
                <span className="text-slate-900 font-mono text-sm">{coverageData.hbse.papers_available} Papers</span>
              </div>
              <div className="flex justify-between font-bold">
                <span className="text-slate-600">Years Covered:</span>
                <span className="text-slate-900 font-mono text-sm">{coverageData.hbse.years_covered} Years</span>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-2xl border-2 border-amber-200 shadow-sm space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="font-black text-amber-950 text-sm">{coverageData.cbse.board_name}</h3>
              <span className="px-2.5 py-0.5 bg-amber-100 text-amber-950 rounded font-bold text-[10px]">CBSE</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between font-bold">
                <span className="text-slate-600">Papers Available:</span>
                <span className="text-slate-900 font-mono text-sm">{coverageData.cbse.papers_available} Papers</span>
              </div>
              <div className="flex justify-between font-bold">
                <span className="text-slate-600">Years Covered:</span>
                <span className="text-slate-900 font-mono text-sm">{coverageData.cbse.years_covered} Years</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Paper Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4 text-xs">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="font-black text-slate-900 text-sm">Import Genuine PYQ Paper</h3>
              <button onClick={() => setShowImportModal(false)}>
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {importStatusMsg && (
              <div className={`p-3 rounded-xl font-bold ${
                importStatusMsg.success ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'
              }`}>
                {importStatusMsg.text}
              </div>
            )}

            <form onSubmit={handleImportSubmit} className="space-y-3">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Original PDF Filename</label>
                <input
                  type="text"
                  value={importFilename}
                  onChange={e => setImportFilename(e.target.value)}
                  placeholder="e.g. CBSE_Class10_Maths_2023_Set1.pdf"
                  required
                  className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Exam Year</label>
                  <input
                    type="number"
                    value={importYear}
                    onChange={e => setImportYear(Number(e.target.value))}
                    className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Set / Code</label>
                  <input
                    type="text"
                    value={importSet}
                    onChange={e => setImportSet(e.target.value)}
                    placeholder="e.g. Set 1"
                    className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Source Name / Authority</label>
                <input
                  type="text"
                  value={importSource}
                  onChange={e => setImportSource(e.target.value)}
                  placeholder="e.g. Board of School Education Haryana"
                  className="w-full p-2 bg-slate-50 border rounded-lg font-medium"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Source Portal URL (Optional)</label>
                <input
                  type="url"
                  value={importUrl}
                  onChange={e => setImportUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full p-2 bg-slate-50 border rounded-lg font-medium"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 bg-slate-100 rounded-xl font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#0F2C59] hover:bg-blue-900 text-white rounded-xl font-black shadow-xs"
                >
                  Import Paper
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default PYQCorpusManagerPage;
